// Wholesaler portal admin RPCs -- consumed by the operator console
// (`apps/web` Wholesalers module) to manage portal seats, invites,
// visibility toggles, change-request decisions, and backorder review
// releases.
//
// All RPCs live in
// `supabase/migrations/20260627000200_wholesaler_portal_rpcs.sql` and
// are gated by `core.user_has_org_access(...)` server-side.

import { supabase, supabaseEndpoint } from '../client';

export interface InviteChannelResult {
  ok: boolean;
  message_id?: string;
  error?: string;
}

export interface CreatePortalInviteResult {
  ok: boolean;
  invite_id: string;
  token: string;
  wholesaler_id: string;
  wholesaler_name: string;
  accept_url: string;
  auth_user_id: string | null;
  user_preexisted: boolean;
  email?: InviteChannelResult;
  sms?: InviteChannelResult;
}

/**
 * Create a wholesaler portal seat invite via the `wholesalers-admin-invite`
 * edge function. The function:
 *
 *   1. Calls `wholesalers.rpc_admin_create_portal_invite` to persist the
 *      invite row (gated by `core.user_has_org_access` against the
 *      wholesaler's org).
 *   2. Generates a Supabase Auth magic link via
 *      `auth.admin.generateLink({ type: 'invite' })` (or `recovery`
 *      when the auth.users row pre-exists) so the recipient can pick
 *      a password through the central `auth.peptideclients.com/set-password`
 *      flow.
 *   3. Queues + dispatches the `wholesaler_portal_invite` email when
 *      `sendEmail` is true (default) and / or the SMS variant when
 *      `sendSms` is true. Returns per-channel `{ ok, message_id?, error? }`.
 *
 * Server-side dispatch ensures emails go out IMMEDIATELY (the old
 * RPC-only path queued without dispatching, leaving every invite
 * stuck in `core.email_messages` with status='queued').
 */
export async function createWholesalerPortalInvite(input: {
  wholesalerId: string;
  email: string;
  role?: 'owner' | 'admin' | 'buyer' | 'viewer';
  ttlHours?: number;
  phone?: string | null;
  sendEmail?: boolean;
  sendSms?: boolean;
}): Promise<CreatePortalInviteResult> {
  const sb = supabase();
  const { data: sess } = await sb.auth.getSession();
  const accessToken = sess.session?.access_token;
  if (!accessToken) {
    throw new Error('Sign in required.');
  }
  const { url: baseUrl, anonKey } = supabaseEndpoint();
  const url = `${baseUrl.replace(/\/$/, '')}/functions/v1/wholesalers-admin-invite`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      apikey: anonKey,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      wholesaler_id: input.wholesalerId,
      email: input.email,
      role: input.role ?? 'buyer',
      ttl_hours: input.ttlHours ?? 168,
      phone: input.phone ?? null,
      send_email: input.sendEmail !== false,
      send_sms: input.sendSms === true,
    }),
  });
  const text = await resp.text();
  let parsed: any = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = { ok: false, error: text || 'invalid_response' };
  }
  if (!resp.ok || parsed?.ok === false) {
    const msg = parsed?.error ?? `HTTP ${resp.status}`;
    throw new Error(msg);
  }
  return parsed as CreatePortalInviteResult;
}

export async function revokeWholesalerPortalInvite(inviteId: string): Promise<{ ok: boolean }> {
  const { data, error } = await (supabase() as any)
    .schema('wholesalers')
    .rpc('rpc_admin_revoke_portal_invite', { p_invite_id: inviteId });
  if (error) throw error;
  return data as { ok: boolean };
}

export async function removeWholesalerPortalSeat(seatId: string): Promise<{ ok: boolean }> {
  const { data, error } = await (supabase() as any)
    .schema('wholesalers')
    .rpc('rpc_admin_remove_portal_seat', { p_seat_id: seatId });
  if (error) throw error;
  return data as { ok: boolean };
}

export async function decideWholesalerChangeRequest(input: {
  changeRequestId: string;
  decision: 'approved' | 'rejected' | 'applied' | 'cancelled';
  notes?: string;
}): Promise<{ ok: boolean }> {
  const { data, error } = await (supabase() as any)
    .schema('wholesalers')
    .rpc('rpc_admin_decide_change_request', {
      p_change_request_id: input.changeRequestId,
      p_decision: input.decision,
      p_notes: input.notes ?? null,
    });
  if (error) throw error;
  return data as { ok: boolean };
}

export async function releaseWholesalerBackorder(input: {
  backorderId: string;
  notes?: string;
}): Promise<{ ok: boolean }> {
  const { data, error } = await (supabase() as any)
    .schema('wholesalers')
    .rpc('rpc_admin_release_backorder', {
      p_backorder_id: input.backorderId,
      p_notes: input.notes ?? null,
    });
  if (error) throw error;
  return data as { ok: boolean };
}

export async function cancelWholesalerBackorder(input: {
  backorderId: string;
  reason?: string;
}): Promise<{ ok: boolean }> {
  const { data, error } = await (supabase() as any)
    .schema('wholesalers')
    .rpc('rpc_admin_cancel_backorder', {
      p_backorder_id: input.backorderId,
      p_reason: input.reason ?? null,
    });
  if (error) throw error;
  return data as { ok: boolean };
}

export async function addWholesalerOperatorNote(input: {
  wholesalerId: string;
  scope: 'account' | 'order' | 'client';
  body: string;
  orderId?: string | null;
  clientId?: string | null;
  visibleToPortal?: boolean;
  threadRootId?: string | null;
}): Promise<{ ok: boolean; note_id: string }> {
  const { data, error } = await (supabase() as any)
    .schema('wholesalers')
    .rpc('rpc_admin_add_note', {
      p_wholesaler_id: input.wholesalerId,
      p_scope: input.scope,
      p_body: input.body,
      p_order_id: input.orderId ?? null,
      p_client_id: input.clientId ?? null,
      p_visible_to_portal: input.visibleToPortal ?? true,
      p_thread_root_id: input.threadRootId ?? null,
    });
  if (error) throw error;
  return data as { ok: boolean; note_id: string };
}

export async function setWholesalerOrderPortalVisibility(input: {
  orderId: string;
  visible: boolean;
}): Promise<{ ok: boolean }> {
  const { data, error } = await (supabase() as any)
    .schema('wholesalers')
    .rpc('rpc_admin_set_order_visibility', {
      p_order_id: input.orderId,
      p_visible: input.visible,
    });
  if (error) throw error;
  return data as { ok: boolean };
}

export async function setWholesalerPaymentPortalVisibility(input: {
  paymentId: string;
  visible: boolean;
}): Promise<{ ok: boolean }> {
  const { data, error } = await (supabase() as any)
    .schema('wholesalers')
    .rpc('rpc_admin_set_payment_visibility', {
      p_payment_id: input.paymentId,
      p_visible: input.visible,
    });
  if (error) throw error;
  return data as { ok: boolean };
}
